<template>
  <ion-app>
    <Header v-if="showGlobalHeader" />
    <ion-router-outlet />
    <div class="seo-cta-buttons" v-if="showSeoButtons">
      <a
        class="seo-cta-btn whatsapp"
        href="https://wa.me/919946395006"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with RiderFieldMotors on WhatsApp"
      >
        <svg class="seo-cta-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2a10 10 0 0 0-8.8 14.8L2 22l5.4-1.2A10 10 0 1 0 12 2zm0 2a8 8 0 0 1 0 16 8 8 0 0 1-4.1-1.1l-.4-.2-2.9.6.6-2.8-.2-.4A8 8 0 0 1 12 4zm-3 4.2c-.2 0-.5.1-.7.4-.3.3-.9.9-.9 2.1s1 2.3 1.1 2.4c.2.2 1.9 3 4.6 4.1.6.3 1.1.4 1.5.5.6.2 1.1.1 1.5.1.5-.1 1.4-.6 1.6-1.2.2-.6.2-1 .1-1.1-.1-.1-.2-.2-.5-.3l-1.6-.8c-.2-.1-.4-.1-.5.1l-.7.9c-.1.1-.3.2-.5.1-.2-.1-1-.4-1.9-1.2-.7-.6-1.2-1.4-1.4-1.7-.1-.2 0-.3.1-.4l.4-.5c.1-.1.2-.3.3-.4.1-.2 0-.3 0-.4l-.7-1.7c-.2-.4-.4-.4-.6-.4H9z"/>
        </svg>
        <span>WhatsApp</span>
      </a>
      <a class="seo-cta-btn call" href="tel:+919946395006" aria-label="Call RiderFieldMotors now">
        <svg class="seo-cta-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.24c1.1.36 2.3.56 3.5.56a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.3 21 3 13.7 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.2.2 2.4.56 3.5a1 1 0 0 1-.24 1z"/>
        </svg>
        <span>Call Now</span>
      </a>
    </div>
    <div class="scroll-btns">
      <transition name="sb">
        <button
          v-show="showTop"
          class="scroll-btn top-btn"
          aria-label="Scroll to top"
          data-label="Top"
          @click="scrollTop"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 19V5M5 12l7-7 7 7"/>
          </svg>
        </button>
      </transition>
      <transition name="sb">
        <button
          v-show="showBottom"
          class="scroll-btn bottom-btn"
          aria-label="Scroll to bottom"
          data-label="Bottom"
          @click="scrollBottom"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 5v14M5 12l7 7 7-7"/>
          </svg>
        </button>
      </transition>
    </div>
  </ion-app>
</template>

<script>
import { IonApp, IonRouterOutlet, toastController } from '@ionic/vue'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import Header from './components/Header.vue'
import { mapGetters } from 'vuex'

const EXIT_ROUTES = ['/home', '/login', '/admin/login', '/admin/panel', '/staff/panel']

export default {
  components: { IonApp, IonRouterOutlet, Header },
  data() {
    return {
      showTop: false,
      showBottom: false,
      scrollEl: null,
      onScroll: null,
      lastBackTap: 0,
      backButtonListener: null,
    }
  },
  computed: {
    ...mapGetters('auth', ['isLoggedIn']),
    isPanelRoute() {
      return this.$route.path.startsWith('/admin/panel') || this.$route.path.startsWith('/staff/panel')
    },
    hideGlobalChrome() {
      return !!this.$route?.meta?.hideGlobalChrome
    },
    showGlobalHeader() {
      return !this.isPanelRoute && this.$route.path !== '/login' && !this.hideGlobalChrome
    },
    showSeoButtons() {
      return !this.isPanelRoute && this.$route.path !== '/login' && !this.hideGlobalChrome
    }
  },
  mounted() {
    this.attachContentScroll()
    this.setupBackButtonExit()
  },
  watch: {
    $route() {
      setTimeout(() => this.attachContentScroll(), 0)
    },
  },
  beforeUnmount() {
    if (this.scrollEl && this.onScroll) {
      this.scrollEl.removeEventListener('scroll', this.onScroll)
    }
    window.removeEventListener('scroll', this.onScroll || (() => {}))
    if (this.backButtonListener) {
      this.backButtonListener.remove()
    }
  },
  methods: {
    async setupBackButtonExit() {
      if (!Capacitor.isNativePlatform()) return
      this.backButtonListener = await App.addListener('backButton', async (data) => {
        const path = this.$route.path
        // Check if current route is a root route or we have no history to go back to
        const isExitRoute = EXIT_ROUTES.includes(path) || path === '/'
        
        if (isExitRoute && !data.canGoBack) {
          const now = Date.now()
          if (now - this.lastBackTap < 2000) {
            App.exitApp()
          } else {
            this.lastBackTap = now
            const toast = await toastController.create({
              message: 'Press back again to exit',
              duration: 2000,
              position: 'bottom',
              cssClass: 'custom-exit-toast'
            })
            await toast.present()
          }
        } else {
          // Normal back navigation
          window.history.back()
        }
      })
    },
    attachContentScroll() {
      if (this.scrollEl && this.onScroll) {
        this.scrollEl.removeEventListener('scroll', this.onScroll)
        this.scrollEl = null
      }
      window.removeEventListener('scroll', this.onScroll || (() => {}))
      this.onScroll = () => {
        const el = this.scrollEl
        if (el) {
          const y = el.scrollTop || 0
          const h = el.scrollHeight || 0
          const vh = el.clientHeight || 0
          this.showTop = y > 300
          this.showBottom = h - (y + vh) > 300
        } else {
          const y = window.pageYOffset || document.documentElement.scrollTop || 0
          const h = document.documentElement.scrollHeight || 0
          const vh = window.innerHeight || 0
          this.showTop = y > 300
          this.showBottom = h - (y + vh) > 300
        }
      }
      const content = document.querySelector('ion-content')
      if (content && typeof content.getScrollElement === 'function') {
        content.getScrollElement().then((el) => {
          this.scrollEl = el
          el.addEventListener('scroll', this.onScroll, { passive: true })
          this.onScroll()
        }).catch(() => {
          window.addEventListener('scroll', this.onScroll, { passive: true })
          this.onScroll()
        })
      } else {
        window.addEventListener('scroll', this.onScroll, { passive: true })
        this.onScroll()
      }
    },
    scrollTop() {
      const content = document.querySelector('ion-content')
      if (content && typeof content.scrollToTop === 'function') {
        content.scrollToTop(500)
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    scrollBottom() {
      const content = document.querySelector('ion-content')
      if (content && typeof content.scrollToBottom === 'function') {
        content.scrollToBottom(500)
      } else {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
      }
    },
  },
}
</script>

<style>
.scroll-btns {
  position: fixed;
  right: 18px;
  bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 9999;
}

.seo-cta-buttons {
  position: fixed;
  left: 14px;
  bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 9999;
}

.seo-cta-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  min-width: 110px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  padding: 10px 16px;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18);
}
.seo-cta-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}
.seo-cta-icon path {
  fill: currentColor;
  stroke: currentColor;
  stroke-width: 0.2;
}

.seo-cta-btn.whatsapp {
  background: #25d366;
  color: #fff;
}

.seo-cta-btn.call {
  background: #0b1c30;
  color: #fff;
}
.scroll-btn {
  width: 46px;
  height: 46px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #daa94a 0%, #b8831e 100%);
  color: #0b1c30;
  box-shadow: 0 10px 28px rgba(218,169,74,0.35);
  transition: transform 0.18s, opacity 0.18s, box-shadow 0.18s, filter 0.18s;
  backdrop-filter: saturate(120%) blur(2px);
  position: relative;
  overflow: hidden;
  will-change: transform, box-shadow, opacity;
}
.scroll-btn:hover {
  transform: translateY(-2px);
  opacity: 0.96;
  box-shadow: 0 14px 36px rgba(218,169,74,0.45);
}
.scroll-btn svg { color: #0b1c30; }
.top-btn svg { transition: transform 0.18s; }
.top-btn:hover svg { transform: translateY(-1px); }
.bottom-btn svg { transition: transform 0.18s; }
.bottom-btn:hover svg { transform: translateY(1px); }

/* Gradient ring glow */
.scroll-btn::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: inherit;
  padding: 2px;
  background: conic-gradient(from 180deg, rgba(218,169,74,0.0), rgba(218,169,74,0.35), rgba(200,48,60,0.28), rgba(218,169,74,0.0));
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  animation: ringSpin 2.6s linear infinite;
}
@keyframes ringSpin {
  to { transform: rotate(360deg); }
}

/* Shine sweep */
.scroll-btn::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -20%;
  width: 200%;
  height: 200%;
  background: linear-gradient(120deg, transparent 20%, rgba(255,255,255,0.15) 35%, transparent 50%);
  transform: translateX(-100%);
  animation: shine 3.6s ease-in-out infinite;
}
@keyframes shine {
  0% { transform: translateX(-120%); }
  50% { transform: translateX(0%); }
  100% { transform: translateX(120%); }
}

/* Tooltip label */
.scroll-btn:hover::before { filter: drop-shadow(0 0 6px rgba(218,169,74,0.35)); }
.scroll-btn[data-label]::marker { content: ''; }
.scroll-btn[data-label]::tooltip { content: ''; }
.scroll-btn[data-label]::after { pointer-events: none; }
.scroll-btn::selection { background: transparent; }
.scroll-btn::backdrop { background: transparent; }
.scroll-btn::spelling-error { text-decoration: none; }
.scroll-btn::grammar-error { text-decoration: none; }
.scroll-btn[data-label]::part(label) { display: none; }
.scroll-btn[data-label]:hover .label { display: block; }

/* Appear/disappear transition */
.sb-enter-active, .sb-leave-active { transition: opacity 0.25s, transform 0.25s; }
.sb-enter-from, .sb-leave-to { opacity: 0; transform: translateY(6px); }

@media (max-width: 560px) {
  .scroll-btns { bottom: 20px; right: 14px; }
  .seo-cta-buttons { bottom: 20px; left: 10px; }
  .seo-cta-btn { min-width: 96px; font-size: 12px; }
}

/* Custom Exit Toast Styling */
.custom-exit-toast {
  --background: rgba(0, 0, 0, 0.85);
  --color: #fff;
  --border-radius: 100px;
  --button-color: #fff;
  font-weight: 500;
  text-align: center;
  margin-bottom: 20px;
}
.custom-exit-toast::part(message) {
  padding: 10px 20px;
}
</style>
